<template>
  <div class="container mt-4">
    <div class="row justify-content-center">
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h2 class="card-title mb-4">Editar Perfil</h2>
            <p class="text-muted small mb-4">
              <router-link to="/grupo">
                <i class="bi bi-people-fill me-1"></i>Gestionar grupo familiar
              </router-link>
            </p>
            
            <form @submit.prevent="actualizarPerfil">
              <!-- Alerta de éxito -->
              <div 
                v-if="mensaje" 
                class="alert alert-success alert-dismissible fade show" 
                role="alert"
              >
                {{ mensaje }}
                <button 
                  type="button" 
                  class="btn-close" 
                  @click="mensaje = ''"
                  aria-label="Close"
                ></button>
              </div>

              <!-- Alerta de error -->
              <div 
                v-if="error" 
                class="alert alert-danger alert-dismissible fade show" 
                role="alert"
              >
                {{ error }}
                <button 
                  type="button" 
                  class="btn-close" 
                  @click="error = ''"
                  aria-label="Close"
                ></button>
              </div>

              <!-- Campo Nombre -->
              <div class="mb-3">
                <label for="nombre" class="form-label">Nombre de Usuario</label>
                <input
                  type="text"
                  class="form-control"
                  id="nombre"
                  v-model="formData.nombre"
                  required
                  :class="{ 'is-invalid': nombreError }"
                >
                <div class="invalid-feedback" v-if="nombreError">
                  {{ nombreError }}
                </div>
              </div>

              <!-- Campo Email -->
              <div class="mb-3">
                <label for="email" class="form-label">Email</label>
                <input
                  type="email"
                  class="form-control"
                  id="email"
                  v-model="formData.email"
                  required
                  :class="{ 'is-invalid': emailError }"
                >
                <div class="invalid-feedback" v-if="emailError">
                  {{ emailError }}
                </div>
              </div>

              <!-- Vista por defecto al iniciar -->
              <div class="mb-3">
                <label for="vista_inicio" class="form-label">Vista por defecto al iniciar</label>
                <select
                  id="vista_inicio"
                  class="form-select"
                  v-model="formData.vista_inicio"
                >
                  <option v-for="opt in opcionesVista" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <div class="form-text">
                  Se usará al entrar en la app y al hacer clic en el icono "Vida".
                </div>
              </div>

              <!-- FIRE — valores por defecto -->
              <div class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-fire me-1 text-danger"></i>FIRE (predeterminados)</h3>
                <p class="text-muted small mb-3">
                  <span v-if="isAdmin">El patrimonio actual <strong>no</strong> se guarda aquí: en Evolución se usa el importe total de la hoja.</span>
                  <span v-else>Indica tu patrimonio actual estimado y el resto de supuestos; se aplicarán al abrir la calculadora FIRE.</span>
                </p>
                <div class="row g-2">
                  <div class="col-md-6" v-if="!isAdmin">
                    <label class="form-label small mb-0">Patrimonio actual (€)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.patrimonioActual" min="0" step="500">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Año de nacimiento</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.anoNacimiento" min="1940" max="2010">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Beneficio latente (%)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.beneficioLatente" min="0" max="100" step="1">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Aportación base/mes (€ de hoy)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.aportacionMes" min="0" step="50">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Retiro mensual tras FIRE (€ de hoy)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.retiroMensualPostFire" min="0" step="50">
                    <div class="form-text small">0 hasta el FIRE; mismo criterio que el gasto objetivo (crece con IPC en la simulación; editable por año en la tabla).</div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Rentabilidad nominal (%)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.rentabilidad" min="0" max="20" step="0.1">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Inflación (%)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.inflacion" min="0" max="10" step="0.1">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Gasto mensual objetivo (€ de hoy)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.gastoMes" min="0" step="50">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Tasa retiro FIRE (%)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.tasaRetiro" min="1" max="8" step="0.1">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Retención fiscal plusvalía (%)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.retencionFiscal" min="0" max="50" step="0.5">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-0">Horizonte (años)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="fireForm.horizonte" min="5" max="80" step="1">
                  </div>
                </div>
              </div>

              <!-- FitDays (solo admin) -->
              <div v-if="isAdmin" class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-speedometer2 me-1 text-primary"></i>FitDays (báscula)</h3>
                <p class="text-muted small mb-3">
                  Credenciales de la app FitDays para sincronizar peso y composición corporal en
                  <router-link to="/salud-fitdays">Salud &gt; FitDays</router-link>.
                  Se guardan cifradas. La sincronización reutiliza la sesión y solo vuelve a entrar si FitDays rechaza el token, sin pedir la contraseña otra vez.
                </p>
                <div v-if="mensajeFitdays" class="alert alert-success py-2 small">{{ mensajeFitdays }}</div>
                <div v-if="errorFitdays" class="alert alert-danger py-2 small">{{ errorFitdays }}</div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Email FitDays</label>
                  <input
                    type="email"
                    class="form-control form-control-sm"
                    v-model="fitdaysForm.email"
                    autocomplete="username"
                  >
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Contraseña FitDays</label>
                  <input
                    type="password"
                    class="form-control form-control-sm"
                    v-model="fitdaysForm.password"
                    :placeholder="fitdaysConfig.hasPassword ? 'Dejar en blanco para no cambiar' : 'Obligatoria la primera vez'"
                    autocomplete="current-password"
                  >
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Región del servidor</label>
                  <select v-model="fitdaysForm.region" class="form-select form-select-sm">
                    <option value="eu">Europa (recomendado)</option>
                    <option value="us">América</option>
                    <option value="cn">China</option>
                  </select>
                </div>
                <div class="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="loadingFitdays"
                    @click="guardarFitdays"
                  >
                    <span v-if="loadingFitdays" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar credenciales FitDays
                  </button>
                  <button
                    v-if="fitdaysConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingFitdays"
                    @click="desconectarFitdays"
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              <!-- Coros (solo admin) -->
              <div v-if="isAdmin" class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-smartwatch me-1 text-primary"></i>Coros</h3>
                <p class="text-muted small mb-3">
                  Credenciales de Coros Training Hub. La app consulta el MCP oficial
                  (<code>queryRecoveryStatus</code>, carga, estado de carrera y <code>querySleepOverview</code>)
                  y sincroniza entrenos en
                  <router-link to="/salud-coros">Salud &gt; Coros</router-link>.
                </p>
                <div v-if="mensajeCoros" class="alert alert-success py-2 small">{{ mensajeCoros }}</div>
                <div v-if="errorCoros" class="alert alert-danger py-2 small">{{ errorCoros }}</div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Email Coros</label>
                  <input type="email" class="form-control form-control-sm" v-model="corosForm.email" autocomplete="username">
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Contraseña Coros</label>
                  <input
                    type="password"
                    class="form-control form-control-sm"
                    v-model="corosForm.password"
                    :placeholder="corosConfig.hasPassword ? 'Dejar en blanco para no cambiar' : 'Obligatoria la primera vez'"
                    autocomplete="current-password"
                  >
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Región</label>
                  <select v-model="corosForm.region" class="form-select form-select-sm">
                    <option value="eu">Europa</option>
                    <option value="us">América</option>
                    <option value="asia">Asia</option>
                  </select>
                </div>
                <div class="d-flex gap-2 flex-wrap">
                  <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingCoros" @click="guardarCoros">
                    <span v-if="loadingCoros" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar credenciales Coros
                  </button>
                  <button
                    v-if="corosConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingCoros"
                    @click="desconectarCoros"
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              <!-- TrainingPeaks (solo admin) -->
              <div v-if="isAdmin" class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-calendar2-week me-1 text-primary"></i>TrainingPeaks</h3>
                <p class="text-muted small mb-3">
                  Pega la cookie <code>Production_tpAuth</code> de
                  <a href="https://app.trainingpeaks.com" target="_blank" rel="noopener">app.trainingpeaks.com</a>
                  (herramientas de desarrollador → Aplicación → Cookies).
                  Se guarda cifrada y solo se usa para leer el calendario, las notas y los comentarios en
                  <router-link to="/salud-trainingpeaks">Salud &gt; TrainingPeaks</router-link>.
                  La copia se renueva cada hora. Si caduca, vuelve a pegarla.
                </p>
                <div v-if="mensajeTp" class="alert alert-success py-2 small">{{ mensajeTp }}</div>
                <div v-if="errorTp" class="alert alert-danger py-2 small">{{ errorTp }}</div>
                <div v-if="tpConfig.athleteName" class="small mb-2">Conectado como {{ tpConfig.athleteName }}</div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Cookie Production_tpAuth</label>
                  <input
                    type="password"
                    class="form-control form-control-sm"
                    v-model="tpForm.cookie"
                    :placeholder="tpConfig.configured ? 'Pegar solo si quieres sustituirla' : 'Pega aquí el valor de la cookie'"
                    autocomplete="off"
                  >
                </div>
                <div class="d-flex gap-2">
                  <button type="button" class="btn btn-sm btn-primary" :disabled="loadingTp" @click="guardarTp">
                    <span v-if="loadingTp" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar TrainingPeaks
                  </button>
                  <button
                    v-if="tpConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingTp"
                    @click="desconectarTp"
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              <!-- TickTick MCP (cada usuario conecta el suyo) -->
              <div class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-check2-circle me-1 text-primary"></i>TickTick (MCP)</h3>
                <p class="text-muted small mb-3">
                  Token del servidor MCP de TickTick. Se guarda cifrado y se usa para mostrar tus tareas, hábitos y
                  eventos de hoy en <router-link to="/mi-dia">Vida &gt; Mi día</router-link>.
                </p>
                <div v-if="mensajeTick" class="alert alert-success py-2 small">{{ mensajeTick }}</div>
                <div v-if="errorTick" class="alert alert-danger py-2 small">{{ errorTick }}</div>
                <div v-if="tickConfig.configured" class="small mb-2">
                  Conectado<span v-if="tickConfig.origen === 'servidor'"> (configurado en el servidor)</span>
                </div>
                <div class="mb-2">
                  <label class="form-label small mb-0">Token</label>
                  <input
                    type="password"
                    class="form-control form-control-sm"
                    v-model="tickForm.token"
                    :placeholder="tickConfig.configured ? 'Pegar solo si quieres sustituirlo' : 'Pega aquí el token'"
                    autocomplete="off"
                  >
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">URL del MCP</label>
                  <input type="url" class="form-control form-control-sm" v-model="tickForm.url" autocomplete="off">
                </div>
                <div class="d-flex gap-2">
                  <button type="button" class="btn btn-sm btn-primary" :disabled="loadingTick" @click="guardarTick">
                    <span v-if="loadingTick" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar TickTick
                  </button>
                  <button
                    v-if="tickConfig.configured && tickConfig.origen === 'perfil'"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingTick"
                    @click="desconectarTick"
                  >
                    Desconectar
                  </button>
                  <button
                    v-if="tickConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-secondary ms-auto"
                    :disabled="loadingTick"
                    @click="diagnosticoTick"
                  >
                    Diagnóstico
                  </button>
                </div>
                <pre v-if="diagnosticoTickTexto" class="small bg-white border rounded p-2 mt-3 mb-0" style="max-height: 320px; overflow: auto; white-space: pre-wrap;">{{ diagnosticoTickTexto }}</pre>
              </div>

              <!-- Google Calendar (cada usuario conecta su cuenta) -->
              <div class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-calendar-event me-1 text-primary"></i>Google Calendar</h3>
                <p class="text-muted small mb-3">
                  Conecta tu cuenta de Google para ver y gestionar tus eventos de hoy en
                  <router-link to="/mi-dia">Vida &gt; Mi día</router-link>. Puedes elegir qué calendarios mostrar
                  y desconectarlo cuando quieras.
                </p>
                <div v-if="errorCal" class="alert alert-danger py-2 small">{{ errorCal }}</div>
                <p v-if="calEstado.cargado && !calEstado.configurado" class="small text-muted mb-0">
                  Google Calendar no está disponible: faltan las credenciales de Google en el servidor.
                </p>
                <template v-else>
                  <div v-if="calEstado.conectado" class="small mb-2">
                    Conectado<span v-if="calEstado.email"> como {{ calEstado.email }}</span>
                  </div>
                  <div class="d-flex gap-2">
                    <button
                      v-if="!calEstado.conectado"
                      type="button"
                      class="btn btn-sm btn-primary"
                      :disabled="loadingCal || !calEstado.cargado"
                      @click="conectarCal"
                    >
                      <span v-if="loadingCal" class="spinner-border spinner-border-sm me-1"></span>
                      Conectar Google Calendar
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      :disabled="loadingCal"
                      @click="desconectarCal"
                    >
                      Desconectar
                    </button>
                  </div>
                </template>
              </div>

              <!-- Kraken (solo admin) -->
              <div v-if="isAdmin" class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-currency-bitcoin me-1 text-primary"></i>Kraken (Cripto MCP)</h3>
                <p class="text-muted small mb-3">
                  Credenciales de la API <strong>Spot</strong> de Kraken (no Futures). Al crear la clave, Kraken muestra dos valores:
                  <strong>API Key</strong> (pública, campo de arriba) y <strong>Private key</strong> (secreta, solo se muestra una vez).
                  Permisos recomendados: Query Funds, Query Trades y Query ledger entries (historial de recompensas en Staking).
                </p>
                <div v-if="mensajeKraken" class="alert alert-success py-2 small">{{ mensajeKraken }}</div>
                <div v-if="errorKraken" class="alert alert-danger py-2 small">{{ errorKraken }}</div>
                <div class="mb-3">
                  <label class="form-label small mb-0">API Key <span class="text-muted">(pública)</span></label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="krakenForm.api_key"
                    :placeholder="krakenConfig.configured && krakenConfig.apiKeyPreview ? `Actual: ${krakenConfig.apiKeyPreview}` : 'La API Key que muestra Kraken arriba'"
                    autocomplete="off"
                  >
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Private key <span class="text-muted">(secreta, base64)</span></label>
                  <input
                    type="password"
                    class="form-control form-control-sm"
                    v-model="krakenForm.api_secret"
                    :placeholder="krakenConfig.hasSecret ? 'Dejar en blanco para no cambiar' : 'La Private key (no confundir con API Key)'"
                    autocomplete="new-password"
                  >
                </div>
                <div class="d-flex gap-2 flex-wrap">
                  <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingKraken" @click="guardarKraken">
                    <span v-if="loadingKraken" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar credenciales Kraken
                  </button>
                  <button
                    v-if="krakenConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingKraken"
                    @click="desconectarKraken"
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              <!-- Trezor watch-only (solo admin) -->
              <div v-if="isAdmin" class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-shield-lock me-1 text-success"></i>Trezor (Cripto MCP)</h3>
                <p class="text-muted small mb-3">
                  Seguimiento <strong>solo lectura</strong> de BTC y ETH en tu hardware wallet. No pegues la seed ni claves privadas.
                  En <a href="https://suite.trezor.io/" target="_blank" rel="noopener">Trezor Suite</a>:
                  cuenta BTC → <em>Detalles</em> → clave pública (xpub/zpub); cuenta ETH → dirección de recepción (0x…).
                  <strong>Una xpub o dirección por línea</strong> (puedes añadir varias cuentas).
                </p>
                <div v-if="mensajeOnchain" class="alert alert-success py-2 small">{{ mensajeOnchain }}</div>
                <div v-if="errorOnchain" class="alert alert-danger py-2 small">{{ errorOnchain }}</div>
                <div class="mb-3">
                  <label class="form-label small mb-0">
                    xpub BTC <span class="text-muted">(una por línea)</span>
                    <span v-if="onchainConfig.btcCount" class="badge bg-secondary ms-1">{{ onchainConfig.btcCount }} guardada(s)</span>
                  </label>
                  <textarea
                    class="form-control form-control-sm font-monospace"
                    v-model="onchainForm.btc_xpubs_text"
                    rows="3"
                    placeholder="zpub…&#10;xpub…"
                    autocomplete="off"
                  ></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">
                    Direcciones ETH <span class="text-muted">(una por línea)</span>
                    <span v-if="onchainConfig.ethCount" class="badge bg-secondary ms-1">{{ onchainConfig.ethCount }} guardada(s)</span>
                  </label>
                  <textarea
                    class="form-control form-control-sm font-monospace"
                    v-model="onchainForm.eth_addresses_text"
                    rows="3"
                    placeholder="0x…&#10;0x…"
                    autocomplete="off"
                  ></textarea>
                </div>
                <div class="d-flex gap-2 flex-wrap">
                  <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingOnchain" @click="guardarOnchain">
                    <span v-if="loadingOnchain" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar Trezor
                  </button>
                  <button
                    v-if="onchainConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingOnchain"
                    @click="desconectarOnchain"
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              <!-- Raindrop / Gmail (solo admin) -->
              <div v-if="isAdmin" class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-bookmark-star me-1 text-info"></i>Raindrop (correos)</h3>
                <p class="text-muted small mb-3">
                  Guarda correos de
                  <router-link to="/gmail">Bandeja a 0</router-link>
                  en Raindrop como documento (Markdown) para leerlos en el visor de Raindrop sin abrir Gmail.
                  Crea una app en
                  <a href="https://app.raindrop.io/settings/integrations" target="_blank" rel="noopener">Integrations</a>
                  y copia el <em>Test token</em>. Opcionalmente indica el ID de una colección (por defecto Unsorted).
                </p>
                <div v-if="mensajeRaindrop" class="alert alert-success py-2 small">{{ mensajeRaindrop }}</div>
                <div v-if="errorRaindrop" class="alert alert-danger py-2 small">{{ errorRaindrop }}</div>
                <div v-if="raindropConfig.email || raindropConfig.fullName" class="small mb-2">
                  Cuenta:
                  <strong>{{ raindropConfig.fullName || raindropConfig.email }}</strong>
                  <span v-if="raindropConfig.fullName && raindropConfig.email" class="text-muted">
                    · {{ raindropConfig.email }}
                  </span>
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Test token</label>
                  <input
                    type="password"
                    class="form-control form-control-sm"
                    v-model="raindropForm.token"
                    :placeholder="raindropConfig.hasToken ? 'Dejar en blanco para no cambiar' : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'"
                    autocomplete="new-password"
                  >
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">ID de colección (opcional)</label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="raindropForm.collection_id"
                    :placeholder="raindropConfig.collectionId != null ? `Actual: ${raindropConfig.collectionId}` : 'Vacío = Unsorted (-1)'"
                    autocomplete="off"
                  >
                </div>
                <div class="d-flex gap-2 flex-wrap">
                  <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingRaindrop" @click="guardarRaindrop">
                    <span v-if="loadingRaindrop" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar y probar Raindrop
                  </button>
                  <button
                    v-if="raindropConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingRaindrop"
                    @click="desconectarRaindrop"
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              <!-- Notion / Reflexiones (solo admin) -->
              <div v-if="isAdmin" class="border rounded p-3 mb-3 bg-light">
                <h3 class="h6 mb-2"><i class="bi bi-journal-richtext me-1 text-warning"></i>Notion (Reflexiones)</h3>
                <p class="text-muted small mb-3">
                  Sincroniza tus pensamientos diarios con tu base de datos personal de Notion.
                  Crea una integración interna en
                  <a href="https://www.notion.so/my-integrations" target="_blank" rel="noopener">notion.so/my-integrations</a>
                  (con permiso de leer, insertar y actualizar contenido),
                  compártela con la base <em>Reflexiones</em> (menú ··· → Conectar) y pega aquí el token y el enlace.
                  Luego escribe en
                  <router-link to="/inspiracion?tab=pensamientos">Inspiración &gt; Pensamientos</router-link>.
                </p>
                <div v-if="mensajeNotion" class="alert alert-success py-2 small">{{ mensajeNotion }}</div>
                <div v-if="errorNotion" class="alert alert-danger py-2 small">{{ errorNotion }}</div>
                <div v-if="notionConfig.databaseTitle" class="small mb-2">
                  Base detectada: <strong>{{ notionConfig.databaseTitle }}</strong>
                  <span v-if="notionConfig.properties?.date" class="text-muted">
                    · fecha: {{ notionConfig.properties.date }}
                  </span>
                  <span v-if="notionConfig.properties?.content" class="text-muted">
                    · texto: {{ notionConfig.properties.content }}
                  </span>
                  <span v-else class="text-muted"> · el texto se guarda en el cuerpo de la página</span>
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">Token de integración</label>
                  <input
                    type="password"
                    class="form-control form-control-sm"
                    v-model="notionForm.token"
                    :placeholder="notionConfig.hasToken ? 'Dejar en blanco para no cambiar' : 'secret_… o ntn_…'"
                    autocomplete="new-password"
                  >
                </div>
                <div class="mb-3">
                  <label class="form-label small mb-0">URL o ID de la base de datos</label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="notionForm.database_id"
                    :placeholder="notionConfig.databaseId ? `Actual: ${notionConfig.databaseId}` : 'https://www.notion.so/…'"
                    autocomplete="off"
                  >
                </div>
                <div class="d-flex gap-2 flex-wrap">
                  <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingNotion" @click="guardarNotion">
                    <span v-if="loadingNotion" class="spinner-border spinner-border-sm me-1"></span>
                    Guardar y probar Notion
                  </button>
                  <button
                    v-if="notionConfig.configured"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="loadingNotion"
                    @click="desconectarNotion"
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              <!-- Botones -->
              <div class="d-grid gap-2">
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="loading"
                >
                  <span 
                    v-if="loading" 
                    class="spinner-border spinner-border-sm me-2" 
                    role="status"
                  ></span>
                  {{ loading ? 'Actualizando...' : 'Actualizar Perfil' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div class="card mt-4">
          <div class="card-body">
            <h3 class="card-title mb-1">
              <i class="bi bi-bell me-2"></i>Notificaciones en el móvil
            </h3>
            <p class="text-muted small mb-3">
              Vida puede avisarte en el móvil cuando alguien del grupo te añade a un gasto,
              aunque no tengas la app abierta. Hay que aceptar el permiso en este dispositivo.
              Una vez activadas, pulsa «Enviar aviso de prueba» para comprobar que llegan.
            </p>

            <div v-if="mensajePush" :class="['alert', mensajePush.tipo, 'alert-dismissible']">
              <div>{{ mensajePush.texto }}</div>
              <button type="button" class="btn-close" @click="mensajePush = null"></button>
            </div>

            <p v-if="pushEstado.ios && !pushEstado.supported" class="small text-muted mb-3">
              En iPhone, Chrome no permite notificaciones web. Seguirás recibiendo el correo,
              o puedes instalar la app desde Safari en la pantalla de inicio.
            </p>
            <p v-else-if="!pushEstado.supported" class="small text-muted mb-3">
              Este navegador no admite notificaciones push.
            </p>
            <p v-else-if="pushEstado.permission === 'denied'" class="small text-danger mb-3">
              Las notificaciones están bloqueadas. En Chrome: icono del candado → Permisos → Notificaciones. En la app Android: Ajustes → Apps → Vida → Notificaciones.
            </p>
            <p v-else-if="pushEstado.permission === 'granted' && pushEstado.suscritoServidor" class="small text-success mb-3">
              Este dispositivo recibirá un aviso al añadir un gasto compartido.
            </p>

            <div class="d-flex flex-wrap gap-2">
              <button
                v-if="pushEstado.supported && pushEstado.permission !== 'granted'"
                type="button"
                class="btn btn-primary"
                :disabled="loadingPush"
                @click="activarNotificacionesMovil"
              >
                <span v-if="loadingPush" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="bi bi-bell-fill me-2"></i>
                Activar notificaciones
              </button>
              <button
                v-if="pushEstado.supported && pushEstado.permission === 'granted'"
                type="button"
                class="btn btn-outline-primary"
                :disabled="loadingPush"
                @click="enviarNotificacionPrueba"
              >
                <span v-if="loadingPush" class="spinner-border spinner-border-sm me-2"></span>
                Enviar aviso de prueba
              </button>
              <button
                v-if="pushEstado.supported && pushEstado.permission === 'granted'"
                type="button"
                class="btn btn-outline-secondary"
                :disabled="loadingPush"
                @click="desactivarNotificacionesMovil"
              >
                Desactivar en este dispositivo
              </button>
            </div>
          </div>
        </div>

        <div class="card mt-4">
          <div class="card-body">
            <h3 class="card-title mb-1">
              <i class="bi bi-android2 me-2"></i>App Android
            </h3>
            <p class="text-muted small mb-0">
              Widget de TrainingPeaks: pulsa prolongadamente el escritorio o el icono de Gastos
              → <strong>Widgets</strong> → Gastos → <strong>Próximos entrenos</strong>.
              Estíralo en vertical y desliza la lista para ver más entrenos.
              Hay que haber iniciado sesión en el teléfono.
            </p>
          </div>
        </div>

        <!-- Sección de Prueba de Correo -->
        <div class="card mt-4">
          <div class="card-body">
            <h3 class="card-title mb-1">Recordatorios por correo</h3>
            <p class="text-muted small mb-3">
              Los recordatorios se envían automáticamente cuando una suscripción con <strong>fecha de fin</strong>
              y <strong>días de recordatorio</strong> configurados está próxima a vencer.
              Usa este botón para verificar que el sistema de correo funciona correctamente.
            </p>

            <div v-if="mensajeEmail" :class="['alert', mensajeEmail.tipo, 'alert-dismissible']">
              <div>{{ mensajeEmail.texto }}</div>
              <div v-if="mensajeEmail.detalle" class="small mt-1 text-muted">{{ mensajeEmail.detalle }}</div>
              <button type="button" class="btn-close" @click="mensajeEmail = null"></button>
            </div>

            <div class="d-grid">
              <button
                type="button"
                class="btn btn-outline-primary"
                @click="probarEmail"
                :disabled="loadingEmail"
              >
                <span v-if="loadingEmail" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="bi bi-envelope-check me-2"></i>
                {{ loadingEmail ? 'Enviando...' : 'Enviar correo de prueba' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Sección de Cambio de Contraseña -->
        <div class="card mt-4">
          <div class="card-body">
            <h3 class="card-title mb-4">Contraseña de {{ formData.nombre }}</h3>
            
            <form @submit.prevent="cambiarContrasena">
              <!-- Alerta de éxito para contraseña -->
              <div 
                v-if="mensajeContrasena" 
                class="alert alert-success alert-dismissible fade show" 
                role="alert"
              >
                {{ mensajeContrasena }}
                <button 
                  type="button" 
                  class="btn-close" 
                  @click="mensajeContrasena = ''"
                  aria-label="Close"
                ></button>
              </div>

              <!-- Alerta de error para contraseña -->
              <div 
                v-if="errorContrasena" 
                class="alert alert-danger alert-dismissible fade show" 
                role="alert"
              >
                {{ errorContrasena }}
                <button 
                  type="button" 
                  class="btn-close" 
                  @click="errorContrasena = ''"
                  aria-label="Close"
                ></button>
              </div>

              <!-- Contraseña actual -->
              <div class="mb-3">
                <label for="contrasenaActual" class="form-label">Contraseña actual</label>
                <input
                  type="password"
                  class="form-control"
                  id="contrasenaActual"
                  v-model="formContrasena.actual"
                  required
                  :class="{ 'is-invalid': contrasenaActualError }"
                >
                <div class="invalid-feedback" v-if="contrasenaActualError">
                  {{ contrasenaActualError }}
                </div>
              </div>

              <!-- Nueva contraseña -->
              <div class="mb-3">
                <label for="nuevaContrasena" class="form-label">Nueva contraseña</label>
                <input
                  type="password"
                  class="form-control"
                  id="nuevaContrasena"
                  v-model="formContrasena.nueva"
                  required
                  :class="{ 'is-invalid': nuevaContrasenaError }"
                >
                <div class="invalid-feedback" v-if="nuevaContrasenaError">
                  {{ nuevaContrasenaError }}
                </div>
                <div class="form-text">
                  Seguridad de la contraseña:
                  <span class="ms-2">
                    <i class="bi bi-circle-fill" :class="getSecurityColor(0)"></i>
                    <i class="bi bi-circle-fill" :class="getSecurityColor(1)"></i>
                    <i class="bi bi-circle-fill" :class="getSecurityColor(2)"></i>
                    <i class="bi bi-circle-fill" :class="getSecurityColor(3)"></i>
                  </span>
                </div>
              </div>

              <!-- Confirmación de la nueva contraseña -->
              <div class="mb-3">
                <label for="confirmarContrasena" class="form-label">Confirmación de la nueva contraseña</label>
                <input
                  type="password"
                  class="form-control"
                  id="confirmarContrasena"
                  v-model="formContrasena.confirmacion"
                  required
                  :class="{ 'is-invalid': confirmacionContrasenaError }"
                >
                <div class="invalid-feedback" v-if="confirmacionContrasenaError">
                  {{ confirmacionContrasenaError }}
                </div>
              </div>

              <!-- Botón de guardar -->
              <div class="d-grid">
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="loadingContrasena"
                >
                  <span 
                    v-if="loadingContrasena" 
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  {{ loadingContrasena ? 'Guardando...' : 'Guardar' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Sección de eliminar cuenta -->
        <div class="card mt-4 border-danger">
          <div class="card-body">
            <h3 class="card-title mb-3 text-danger">Eliminar cuenta</h3>
            <p class="text-muted">
              Se borrarán tu cuenta y todos tus datos de forma permanente (gastos, ingresos, salud,
              tareas, integraciones…). Esta acción no se puede deshacer.
              <router-link to="/privacidad">Más información</router-link>
            </p>
            <div v-if="errorEliminar" class="alert alert-danger">{{ errorEliminar }}</div>
            <form @submit.prevent="eliminarCuenta">
              <div class="mb-3">
                <label for="passwordEliminar" class="form-label">Confirma con tu contraseña</label>
                <input
                  type="password"
                  class="form-control"
                  id="passwordEliminar"
                  v-model="passwordEliminar"
                  autocomplete="current-password"
                  required
                >
              </div>
              <button type="submit" class="btn btn-outline-danger" :disabled="loadingEliminar || !passwordEliminar">
                <span v-if="loadingEliminar" class="spinner-border spinner-border-sm me-2"></span>
                Eliminar mi cuenta definitivamente
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from '../config/axios';
import { normalizeFirePrefs, buildFirePrefsForSave } from '../utils/firePrefs';
import {
  obtenerEstadoPush,
  activarPush,
  desactivarPush,
  enviarPushDePrueba
} from '../utils/pushNotifications';
import { esNativo } from '../config/entorno';
import { APP_VERSION } from '../config/version';

export default {
  name: 'PerfilView',
  
  setup() {
    const store = useStore();
    const isAdmin = computed(() => !!store.getters['auth/isAdmin']);
    const esAppNativa = esNativo();
    const appVersion = APP_VERSION;

    const formData = ref({
      nombre: '',
      email: '',
      vista_inicio: '/'
    });

    const fireForm = ref(normalizeFirePrefs({}));

    const opcionesVista = computed(() => {
      const base = [
        { value: '/', label: 'Gastos' },
        { value: '/resumen', label: 'Resumen' },
        { value: '/gastos-planificados', label: 'Análisis > Gastos planificados' },
        { value: '/suscripciones', label: 'Suscripciones' },
        { value: '/gastos-por-categoria', label: 'Análisis > Por categoría' },
        { value: '/gastos-por-mes', label: 'Análisis > Por mes' },
        { value: '/gastos-por-ano', label: 'Análisis > Por año' },
        { value: '/calculadora-fire', label: 'Análisis > FIRE' },
        { value: '/nutricion', label: 'Salud > Nutrición' },
        { value: '/recetas', label: 'Salud > Recetas' },
        { value: '/lista-compra', label: 'Compra' },
        { value: '/tareas', label: 'Vida > Tareas' },
        { value: '/peliculas', label: 'Vida > Películas y series' },
        { value: '/grupo', label: 'Grupo familiar' },
        { value: '/my-life', label: 'Vida > My Life' },
        { value: '/mi-dia', label: 'Vida > Mi día' }
      ];
      if (store.getters['auth/hasIngresos']) {
        base.splice(1, 0, { value: '/ingresos', label: 'Ingresos' });
      }
      if (store.getters['auth/isAdmin']) {
        base.push({ value: '/salud', label: 'Salud' });
        base.push({ value: '/salud-oura', label: 'Salud > Oura' });
        base.push({ value: '/salud-fitdays', label: 'Salud > FitDays' });
        base.push({ value: '/salud-coros', label: 'Salud > Coros' });
        base.push({ value: '/salud-trainingpeaks', label: 'Salud > TrainingPeaks' });
        base.push({ value: '/fi-cripto', label: 'Análisis > FI y Cripto' });
        base.push({ value: '/cripto-mcp', label: 'Análisis > Cripto MCP' });
        base.push({ value: '/evolucion', label: 'Análisis > Evolución' });
        base.push({ value: '/inspiracion', label: 'Vida > Inspiración' });
        base.push({ value: '/gmail', label: 'Vida > Bandeja' });
        base.push({ value: '/seguimiento', label: 'Vida > Seguimiento' });
        base.push({ value: '/admin', label: 'Administración' });
      }
      return base;
    });
    const loading = ref(false);
    const error = ref('');
    const mensaje = ref('');
    const nombreError = ref('');
    const emailError = ref('');

    const loadingContrasena = ref(false);
    const errorContrasena = ref('');
    const mensajeContrasena = ref('');

    const loadingEmail = ref(false);
    const mensajeEmail = ref(null);
    const loadingPush = ref(false);
    const mensajePush = ref(null);
    const pushEstado = ref({
      supported: esAppNativa,
      permission: esAppNativa ? 'default' : 'unsupported',
      enabled: false,
      suscritoServidor: false,
      ios: false
    });
    const contrasenaActualError = ref('');
    const nuevaContrasenaError = ref('');
    const confirmacionContrasenaError = ref('');

    const formContrasena = ref({
      actual: '',
      nueva: '',
      confirmacion: ''
    });

    const fitdaysForm = ref({ email: '', password: '', region: 'eu' });
    const fitdaysConfig = ref({ configured: false, hasPassword: false });
    const loadingFitdays = ref(false);
    const mensajeFitdays = ref('');
    const errorFitdays = ref('');

    const corosForm = ref({ email: '', password: '', region: 'eu' });
    const corosConfig = ref({ configured: false, hasPassword: false });
    const loadingCoros = ref(false);
    const mensajeCoros = ref('');
    const errorCoros = ref('');

    const tpForm = ref({ cookie: '' });
    const tpConfig = ref({ configured: false, athleteName: '' });
    const loadingTp = ref(false);
    const mensajeTp = ref('');
    const errorTp = ref('');
    const tickForm = ref({ token: '', url: 'https://mcp.ticktick.com' });
    const tickConfig = ref({ configured: false, origen: null });
    const loadingTick = ref(false);
    const mensajeTick = ref('');
    const errorTick = ref('');
    const diagnosticoTickTexto = ref('');
    const calEstado = ref({ cargado: false, configurado: true, conectado: false, email: '' });
    const loadingCal = ref(false);
    const errorCal = ref('');

    const krakenForm = ref({ api_key: '', api_secret: '' });
    const krakenConfig = ref({ configured: false, hasSecret: false });
    const loadingKraken = ref(false);
    const mensajeKraken = ref('');
    const errorKraken = ref('');

    const onchainForm = ref({ btc_xpubs_text: '', eth_addresses_text: '' });
    const onchainConfig = ref({ configured: false, hasBtcXpub: false, hasEthAddress: false, btcCount: 0, ethCount: 0 });
    const loadingOnchain = ref(false);
    const mensajeOnchain = ref('');
    const errorOnchain = ref('');

    const notionForm = ref({ token: '', database_id: '' });
    const notionConfig = ref({ configured: false, hasToken: false, databaseId: '', databaseTitle: '', properties: {} });
    const loadingNotion = ref(false);
    const mensajeNotion = ref('');
    const errorNotion = ref('');

    const raindropForm = ref({ token: '', collection_id: '' });
    const raindropConfig = ref({ configured: false, hasToken: false, collectionId: null, email: '', fullName: '' });
    const loadingRaindrop = ref(false);
    const mensajeRaindrop = ref('');
    const errorRaindrop = ref('');

    const cargarFitdaysConfig = async () => {
      if (!store.getters['auth/isAdmin']) return;
      try {
        const { data } = await axios.get('/api/fitdays/config');
        fitdaysConfig.value = data;
        fitdaysForm.value.email = data.email || '';
        fitdaysForm.value.region = data.region || 'eu';
        fitdaysForm.value.password = '';
      } catch {
        /* ignore si no admin o sin ruta */
      }
    };

    const guardarFitdays = async () => {
      loadingFitdays.value = true;
      mensajeFitdays.value = '';
      errorFitdays.value = '';
      try {
        const body = {
          email: fitdaysForm.value.email.trim(),
          region: fitdaysForm.value.region,
        };
        if (fitdaysForm.value.password) {
          body.password = fitdaysForm.value.password;
        }
        const { data } = await axios.put('/api/fitdays/config', body);
        mensajeFitdays.value = data.message || 'Credenciales guardadas.';
        fitdaysForm.value.password = '';
        await cargarFitdaysConfig();
      } catch (err) {
        errorFitdays.value = err.response?.data?.error || 'Error al guardar FitDays';
      } finally {
        loadingFitdays.value = false;
      }
    };

    const desconectarFitdays = async () => {
      if (!window.confirm('¿Desconectar FitDays y borrar mediciones locales?')) return;
      loadingFitdays.value = true;
      mensajeFitdays.value = '';
      errorFitdays.value = '';
      try {
        await axios.delete('/api/fitdays/config');
        fitdaysForm.value = { email: '', password: '', region: 'eu' };
        fitdaysConfig.value = { configured: false, hasPassword: false };
        mensajeFitdays.value = 'FitDays desconectado.';
      } catch (err) {
        errorFitdays.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingFitdays.value = false;
      }
    };

    const cargarCorosConfig = async () => {
      if (!store.getters['auth/isAdmin']) return;
      try {
        const { data } = await axios.get('/api/coros/config');
        corosConfig.value = data;
        corosForm.value.email = data.email || '';
        corosForm.value.region = data.region || 'eu';
        corosForm.value.password = '';
      } catch {
        /* ignore */
      }
    };

    const guardarCoros = async () => {
      loadingCoros.value = true;
      mensajeCoros.value = '';
      errorCoros.value = '';
      try {
        const body = {
          email: corosForm.value.email.trim(),
          region: corosForm.value.region,
        };
        if (corosForm.value.password) {
          body.password = corosForm.value.password;
        }
        const { data } = await axios.put('/api/coros/config', body);
        mensajeCoros.value = data.message || 'Credenciales guardadas.';
        corosForm.value.password = '';
        await cargarCorosConfig();
      } catch (err) {
        errorCoros.value = err.response?.data?.error || 'Error al guardar Coros';
      } finally {
        loadingCoros.value = false;
      }
    };

    const desconectarCoros = async () => {
      if (!window.confirm('¿Desconectar Coros y borrar datos locales?')) return;
      loadingCoros.value = true;
      mensajeCoros.value = '';
      errorCoros.value = '';
      try {
        await axios.delete('/api/coros/config');
        corosForm.value = { email: '', password: '', region: 'eu' };
        corosConfig.value = { configured: false, hasPassword: false };
        mensajeCoros.value = 'Coros desconectado.';
      } catch (err) {
        errorCoros.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingCoros.value = false;
      }
    };

    const cargarTpConfig = async () => {
      if (!store.getters['auth/isAdmin']) return;
      try {
        const { data } = await axios.get('/api/trainingpeaks/config');
        tpConfig.value = data;
        tpForm.value.cookie = '';
      } catch {
        /* ignore */
      }
    };

    const guardarTp = async () => {
      loadingTp.value = true;
      mensajeTp.value = '';
      errorTp.value = '';
      try {
        if (!tpForm.value.cookie.trim()) {
          errorTp.value = 'Pega la cookie de TrainingPeaks.';
          return;
        }
        const { data } = await axios.put('/api/trainingpeaks/config', { cookie: tpForm.value.cookie.trim() });
        mensajeTp.value = data.message || 'TrainingPeaks conectado.';
        tpForm.value.cookie = '';
        await cargarTpConfig();
      } catch (err) {
        errorTp.value = err.response?.data?.error || 'Error al guardar TrainingPeaks';
      } finally {
        loadingTp.value = false;
      }
    };

    const desconectarTp = async () => {
      if (!window.confirm('¿Desconectar TrainingPeaks y borrar el calendario local?')) return;
      loadingTp.value = true;
      mensajeTp.value = '';
      errorTp.value = '';
      try {
        await axios.delete('/api/trainingpeaks/config');
        tpForm.value.cookie = '';
        tpConfig.value = { configured: false, athleteName: '' };
        mensajeTp.value = 'TrainingPeaks desconectado.';
      } catch (err) {
        errorTp.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingTp.value = false;
      }
    };

    const cargarCalEstado = async () => {
      try {
        const { data } = await axios.get('/api/calendar/status');
        calEstado.value = { ...data, cargado: true };
      } catch {
        calEstado.value = { cargado: true, configurado: false, conectado: false, email: '' };
      }
    };

    const conectarCal = async () => {
      loadingCal.value = true;
      errorCal.value = '';
      try {
        const { data } = await axios.get('/api/calendar/oauth-url');
        window.location.href = data.url;
      } catch (err) {
        errorCal.value = err.response?.data?.error || 'No se pudo iniciar la conexión con Google Calendar';
        loadingCal.value = false;
      }
    };

    const desconectarCal = async () => {
      if (!window.confirm('¿Desconectar Google Calendar?')) return;
      loadingCal.value = true;
      errorCal.value = '';
      try {
        await axios.delete('/api/calendar');
        await cargarCalEstado();
      } catch (err) {
        errorCal.value = err.response?.data?.error || 'No se pudo desconectar';
      } finally {
        loadingCal.value = false;
      }
    };

    const cargarTickConfig = async () => {
      try {
        const { data } = await axios.get('/api/mi-dia/ticktick/config');
        tickConfig.value = data;
        tickForm.value = { token: '', url: data.url || tickForm.value.url };
      } catch {
        /* ignore */
      }
    };

    const guardarTick = async () => {
      loadingTick.value = true;
      mensajeTick.value = '';
      errorTick.value = '';
      try {
        if (!tickForm.value.token.trim()) {
          errorTick.value = 'Pega el token de TickTick.';
          return;
        }
        const { data } = await axios.put('/api/mi-dia/ticktick/config', {
          token: tickForm.value.token.trim(),
          url: tickForm.value.url.trim(),
        });
        mensajeTick.value = data.message || 'TickTick conectado.';
        await cargarTickConfig();
      } catch (err) {
        errorTick.value = err.response?.data?.error || 'Error al guardar TickTick';
      } finally {
        loadingTick.value = false;
      }
    };

    const diagnosticoTick = async () => {
      loadingTick.value = true;
      errorTick.value = '';
      diagnosticoTickTexto.value = '';
      try {
        const { data } = await axios.get('/api/mi-dia/ticktick/diagnostico');
        diagnosticoTickTexto.value = JSON.stringify(data, null, 2);
      } catch (err) {
        errorTick.value = err.response?.data?.error || 'No se pudo ejecutar el diagnóstico';
      } finally {
        loadingTick.value = false;
      }
    };

    const desconectarTick = async () => {
      if (!window.confirm('¿Desconectar TickTick?')) return;
      loadingTick.value = true;
      mensajeTick.value = '';
      errorTick.value = '';
      try {
        await axios.delete('/api/mi-dia/ticktick/config');
        mensajeTick.value = 'TickTick desconectado.';
        await cargarTickConfig();
      } catch (err) {
        errorTick.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingTick.value = false;
      }
    };

    const cargarKrakenConfig = async () => {
      if (!store.getters['auth/isAdmin']) return;
      try {
        const { data } = await axios.get('/api/kraken/config');
        krakenConfig.value = data;
        krakenForm.value.api_key = '';
        krakenForm.value.api_secret = '';
      } catch {
        /* ignore */
      }
    };

    const guardarKraken = async () => {
      loadingKraken.value = true;
      mensajeKraken.value = '';
      errorKraken.value = '';
      try {
        const body = { api_key: krakenForm.value.api_key.trim() };
        if (krakenForm.value.api_secret) {
          body.api_secret = krakenForm.value.api_secret.trim();
        }
        const { data } = await axios.put('/api/kraken/config', body);
        mensajeKraken.value = data.message || 'Credenciales guardadas.';
        krakenForm.value.api_secret = '';
        await cargarKrakenConfig();
      } catch (err) {
        errorKraken.value = err.response?.data?.error || 'Error al guardar Kraken';
      } finally {
        loadingKraken.value = false;
      }
    };

    const desconectarKraken = async () => {
      if (!window.confirm('¿Desconectar Kraken y borrar el historial del agente?')) return;
      loadingKraken.value = true;
      mensajeKraken.value = '';
      errorKraken.value = '';
      try {
        await axios.delete('/api/kraken/config');
        krakenForm.value = { api_key: '', api_secret: '' };
        krakenConfig.value = { configured: false, hasSecret: false };
        mensajeKraken.value = 'Kraken desconectado.';
      } catch (err) {
        errorKraken.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingKraken.value = false;
      }
    };

    const cargarOnchainConfig = async () => {
      if (!store.getters['auth/isAdmin']) return;
      try {
        const { data } = await axios.get('/api/onchain/config');
        onchainConfig.value = data;
        onchainForm.value = {
          btc_xpubs_text: (data.btcXpubs || []).join('\n'),
          eth_addresses_text: (data.ethAddresses || []).join('\n'),
        };
      } catch {
        /* ignore */
      }
    };

    const guardarOnchain = async () => {
      loadingOnchain.value = true;
      mensajeOnchain.value = '';
      errorOnchain.value = '';
      try {
        const btcText = onchainForm.value.btc_xpubs_text.trim();
        const ethText = onchainForm.value.eth_addresses_text.trim();
        if (!btcText && !ethText) {
          errorOnchain.value = 'Indica al menos una xpub BTC y/o una dirección ETH.';
          return;
        }
        const { data } = await axios.put('/api/onchain/config', {
          btc_xpubs_text: btcText,
          eth_addresses_text: ethText,
        });
        mensajeOnchain.value = data.message || 'Trezor guardado.';
        await cargarOnchainConfig();
      } catch (err) {
        errorOnchain.value = err.response?.data?.error || 'Error al guardar Trezor';
      } finally {
        loadingOnchain.value = false;
      }
    };

    const desconectarOnchain = async () => {
      if (!window.confirm('¿Desconectar Trezor y borrar las claves watch-only guardadas?')) return;
      loadingOnchain.value = true;
      mensajeOnchain.value = '';
      errorOnchain.value = '';
      try {
        await axios.delete('/api/onchain/config');
        onchainForm.value = { btc_xpubs_text: '', eth_addresses_text: '' };
        onchainConfig.value = { configured: false, hasBtcXpub: false, hasEthAddress: false, btcCount: 0, ethCount: 0 };
        mensajeOnchain.value = 'Trezor desconectado.';
      } catch (err) {
        errorOnchain.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingOnchain.value = false;
      }
    };

    const cargarNotionConfig = async () => {
      if (!store.getters['auth/isAdmin']) return;
      try {
        const { data } = await axios.get('/api/pensamientos/config');
        notionConfig.value = data;
        notionForm.value = {
          token: '',
          database_id: data.databaseId || '',
        };
      } catch {
        /* ignore */
      }
    };

    const cargarRaindropConfig = async () => {
      if (!store.getters['auth/isAdmin']) return;
      try {
        const { data } = await axios.get('/api/raindrop/config');
        raindropConfig.value = data;
        raindropForm.value = {
          token: '',
          collection_id: data.collectionId != null ? String(data.collectionId) : '',
        };
      } catch {
        /* ignore */
      }
    };

    const guardarRaindrop = async () => {
      loadingRaindrop.value = true;
      mensajeRaindrop.value = '';
      errorRaindrop.value = '';
      try {
        const body = {
          collection_id: raindropForm.value.collection_id.trim() || null,
        };
        if (raindropForm.value.token) {
          body.token = raindropForm.value.token.trim();
        }
        const { data } = await axios.put('/api/raindrop/config', body);
        mensajeRaindrop.value = data.message || 'Raindrop conectado.';
        raindropForm.value.token = '';
        await cargarRaindropConfig();
      } catch (err) {
        errorRaindrop.value = err.response?.data?.error || 'Error al guardar Raindrop';
      } finally {
        loadingRaindrop.value = false;
      }
    };

    const desconectarRaindrop = async () => {
      if (!window.confirm('¿Desconectar Raindrop y borrar el token guardado?')) return;
      loadingRaindrop.value = true;
      mensajeRaindrop.value = '';
      errorRaindrop.value = '';
      try {
        await axios.delete('/api/raindrop/config');
        raindropForm.value = { token: '', collection_id: '' };
        raindropConfig.value = { configured: false, hasToken: false, collectionId: null, email: '', fullName: '' };
        mensajeRaindrop.value = 'Raindrop desconectado.';
      } catch (err) {
        errorRaindrop.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingRaindrop.value = false;
      }
    };

    const guardarNotion = async () => {
      loadingNotion.value = true;
      mensajeNotion.value = '';
      errorNotion.value = '';
      try {
        const body = { database_id: notionForm.value.database_id.trim() };
        if (notionForm.value.token) {
          body.token = notionForm.value.token.trim();
        }
        const { data } = await axios.put('/api/pensamientos/config', body);
        mensajeNotion.value = data.message || 'Notion conectado.';
        notionForm.value.token = '';
        await cargarNotionConfig();
      } catch (err) {
        errorNotion.value = err.response?.data?.error || 'Error al guardar Notion';
      } finally {
        loadingNotion.value = false;
      }
    };

    const desconectarNotion = async () => {
      if (!window.confirm('¿Desconectar Notion? Los pensamientos de la app se mantienen.')) return;
      loadingNotion.value = true;
      mensajeNotion.value = '';
      errorNotion.value = '';
      try {
        await axios.delete('/api/pensamientos/config');
        notionForm.value = { token: '', database_id: '' };
        notionConfig.value = { configured: false, hasToken: false, databaseId: '', databaseTitle: '', properties: {} };
        mensajeNotion.value = 'Notion desconectado.';
      } catch (err) {
        errorNotion.value = err.response?.data?.error || 'Error al desconectar';
      } finally {
        loadingNotion.value = false;
      }
    };

    const cargarEstadoPush = async () => {
      try {
        pushEstado.value = await obtenerEstadoPush();
      } catch {
        /* el botón queda desactivado si falla */
      }
    };

    const activarNotificacionesMovil = async () => {
      loadingPush.value = true;
      mensajePush.value = null;
      try {
        await activarPush((paso) => {
          mensajePush.value = { tipo: 'alert-info', texto: paso };
        });
        await cargarEstadoPush();
        mensajePush.value = {
          tipo: 'alert-success',
          texto: 'Notificaciones activadas. Puedes enviar un aviso de prueba para comprobarlas en el móvil.'
        };
      } catch (err) {
        mensajePush.value = {
          tipo: 'alert-danger',
          texto: err.response?.data?.error || err.message || 'No se pudieron activar las notificaciones'
        };
        await cargarEstadoPush();
      } finally {
        loadingPush.value = false;
      }
    };

    const desactivarNotificacionesMovil = async () => {
      loadingPush.value = true;
      mensajePush.value = null;
      try {
        await desactivarPush();
        await cargarEstadoPush();
        mensajePush.value = {
          tipo: 'alert-success',
          texto: 'Notificaciones desactivadas en este dispositivo.'
        };
      } catch (err) {
        mensajePush.value = {
          tipo: 'alert-danger',
          texto: err.response?.data?.error || err.message || 'No se pudieron desactivar'
        };
      } finally {
        loadingPush.value = false;
      }
    };

    const conLimiteUI = (promesa) => Promise.race([
      promesa,
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('El servidor no respondió al enviar el aviso de prueba.')), 20000);
      })
    ]);

    const enviarNotificacionPrueba = async () => {
      loadingPush.value = true;
      mensajePush.value = null;
      try {
        await activarPush((paso) => {
          mensajePush.value = { tipo: 'alert-info', texto: paso };
        });
        mensajePush.value = { tipo: 'alert-info', texto: 'Enviando aviso de prueba…' };
        const retraso = esAppNativa ? 10 : 0;
        await conLimiteUI(enviarPushDePrueba(retraso));
        await cargarEstadoPush();
        mensajePush.value = {
          tipo: 'alert-success',
          texto: retraso
            ? `Aviso programado en ${retraso} s. Minimiza la app o bloquea la pantalla: con la app abierta Android no muestra la notificación.`
            : 'Aviso de prueba enviado. Debería aparecer en las notificaciones del móvil.'
        };
      } catch (err) {
        mensajePush.value = {
          tipo: 'alert-danger',
          texto: err.response?.data?.error || err.message || 'No se pudo enviar el aviso de prueba'
        };
      } finally {
        loadingPush.value = false;
      }
    };

    // Cargar datos del usuario
    onMounted(() => {
      const user = store.state.auth.user;
      if (user) {
        formData.value.nombre = user.nombre;
        formData.value.email = user.email;
        formData.value.vista_inicio = user.vista_inicio || '/';
        fireForm.value = normalizeFirePrefs(user.fire_prefs || {});
      }
      cargarFitdaysConfig();
      cargarCorosConfig();
      cargarTpConfig();
      cargarTickConfig();
      cargarCalEstado();
      cargarKrakenConfig();
      cargarOnchainConfig();
      cargarNotionConfig();
      cargarRaindropConfig();
      cargarEstadoPush();
    });

    const validarFormulario = () => {
      let isValid = true;
      nombreError.value = '';
      emailError.value = '';

      if (!formData.value.nombre.trim()) {
        nombreError.value = 'El nombre es requerido';
        isValid = false;
      }

      if (!formData.value.email.trim()) {
        emailError.value = 'El email es requerido';
        isValid = false;
      } else if (!formData.value.email.includes('@')) {
        emailError.value = 'El email debe contener @';
        isValid = false;
      }

      return isValid;
    };

    const actualizarPerfil = async () => {
      if (!validarFormulario()) return;

      loading.value = true;
      error.value = '';
      mensaje.value = '';

      try {
        const response = await axios.put('/api/usuarios/perfil', {
          nombre: formData.value.nombre.trim(),
          email: formData.value.email.trim(),
          vista_inicio: formData.value.vista_inicio || '/',
          fire_prefs: buildFirePrefsForSave(fireForm.value, !!store.getters['auth/isAdmin'])
        });

        // Actualizar el token y la información del usuario en el store
        store.commit('auth/setToken', response.data.token);
        // Fusionar con usuario actual para preservar es_admin e ingresos_habilitado si la respuesta los omitiera
        const userActual = store.state.auth.user;
        const userRespuesta = response.data.user || {};
        const userCompleto = {
          ...userActual,
          ...userRespuesta,
          es_admin: userRespuesta.es_admin ?? userActual?.es_admin ?? 0,
          ingresos_habilitado: userRespuesta.ingresos_habilitado ?? userActual?.ingresos_habilitado ?? 0,
          fire_prefs: userRespuesta.fire_prefs != null ? userRespuesta.fire_prefs : userActual?.fire_prefs
        };
        store.commit('auth/setUser', userCompleto);

        mensaje.value = 'Perfil actualizado correctamente';
      } catch (err) {
        console.error('Error al actualizar perfil:', err);
        if (err.code === 'ECONNABORTED') {
          error.value = 'La operación ha tardado demasiado tiempo. Por favor, inténtalo de nuevo.';
        } else if (err.response?.data?.error) {
          error.value = err.response.data.error;
        } else {
          error.value = 'Error al actualizar el perfil. Por favor, inténtalo de nuevo.';
        }
      } finally {
        loading.value = false;
      }
    };

    const evaluarSeguridadContrasena = (password) => {
      let puntuacion = 0;

      // Longitud mínima de 8 caracteres
      if (password.length >= 8) puntuacion++;

      // Contiene números y letras
      if (/(?=.*[0-9])(?=.*[a-zA-Z])/.test(password)) puntuacion++;

      // Contiene mayúsculas y minúsculas
      if (/(?=.*[a-z])(?=.*[A-Z])/.test(password)) puntuacion++;

      // Contiene caracteres especiales
      if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) puntuacion++;

      return puntuacion;
    };

    const getSecurityColor = (nivel) => {
      const seguridadActual = evaluarSeguridadContrasena(formContrasena.value.nueva);
      
      if (seguridadActual === 0) return 'text-secondary';
      
      // Asignar color según el nivel actual y la posición del círculo
      if (nivel <= seguridadActual - 1) {
        switch (seguridadActual) {
          case 1: return 'text-danger';     // Débil - solo el primer círculo rojo
          case 2: return 'text-warning';    // Media - dos primeros círculos amarillos
          case 3: return 'text-info';       // Buena - tres primeros círculos azules
          case 4: return 'text-success';    // Excelente - todos los círculos verdes
        }
      }
      return 'text-secondary';
    };

    const validarFormularioContrasena = () => {
      let isValid = true;
      contrasenaActualError.value = '';
      nuevaContrasenaError.value = '';
      confirmacionContrasenaError.value = '';

      if (!formContrasena.value.actual) {
        contrasenaActualError.value = 'La contraseña actual es requerida';
        isValid = false;
      }

      if (!formContrasena.value.nueva) {
        nuevaContrasenaError.value = 'La nueva contraseña es requerida';
        isValid = false;
      } else {
        const seguridadContrasena = evaluarSeguridadContrasena(formContrasena.value.nueva);
        if (seguridadContrasena < 3) {
          nuevaContrasenaError.value = 'La contraseña debe ser más segura (mínimo 8 caracteres, mayúsculas, minúsculas, números y caracteres especiales)';
          isValid = false;
        }
      }

      if (!formContrasena.value.confirmacion) {
        confirmacionContrasenaError.value = 'La confirmación de la contraseña es requerida';
        isValid = false;
      } else if (formContrasena.value.nueva !== formContrasena.value.confirmacion) {
        confirmacionContrasenaError.value = 'Las contraseñas no coinciden';
        isValid = false;
      }

      return isValid;
    };

    const probarEmail = async () => {
      loadingEmail.value = true;
      mensajeEmail.value = null;
      try {
        const response = await axios.post('/api/usuarios/test-email');
        mensajeEmail.value = {
          tipo: 'alert-success',
          texto: response.data.message,
          detalle: `De: ${response.data.emailFrom} → Para: ${response.data.emailTo}`
        };
      } catch (err) {
        const data = err.response?.data;
        mensajeEmail.value = {
          tipo: 'alert-danger',
          texto: data?.message || 'Error al enviar el correo de prueba',
          detalle: data?.hint || data?.error || null
        };
      } finally {
        loadingEmail.value = false;
      }
    };

    const router = useRouter();
    const passwordEliminar = ref('');
    const loadingEliminar = ref(false);
    const errorEliminar = ref('');

    const eliminarCuenta = async () => {
      if (!window.confirm('¿Seguro? Se eliminarán tu cuenta y todos tus datos de forma permanente.')) return;
      loadingEliminar.value = true;
      errorEliminar.value = '';
      try {
        await axios.delete('/api/usuarios/cuenta', { data: { password: passwordEliminar.value } });
        store.commit('auth/logout');
        store.commit('clearData');
        store.commit('suscripciones/clearSuscripciones');
        router.push('/login');
      } catch (err) {
        errorEliminar.value = err.response?.data?.error || 'No se pudo eliminar la cuenta';
      } finally {
        loadingEliminar.value = false;
      }
    };

    const cambiarContrasena = async () => {
      if (!validarFormularioContrasena()) return;

      loadingContrasena.value = true;
      errorContrasena.value = '';
      mensajeContrasena.value = '';

      try {
        await axios.put('/api/usuarios/cambiar-contrasena', {
          contrasenaActual: formContrasena.value.actual,
          nuevaContrasena: formContrasena.value.nueva
        });

        mensajeContrasena.value = 'Contraseña actualizada correctamente';
        formContrasena.value = {
          actual: '',
          nueva: '',
          confirmacion: ''
        };
      } catch (err) {
        console.error('Error al cambiar contraseña:', err);
        errorContrasena.value = err.response?.data?.error || 'Error al cambiar la contraseña';
      } finally {
        loadingContrasena.value = false;
      }
    };

    return {
      formData,
      fireForm,
      isAdmin,
      esAppNativa,
      appVersion,
      opcionesVista,
      loading,
      error,
      mensaje,
      nombreError,
      emailError,
      actualizarPerfil,
      formContrasena,
      loadingContrasena,
      errorContrasena,
      mensajeContrasena,
      contrasenaActualError,
      nuevaContrasenaError,
      confirmacionContrasenaError,
      cambiarContrasena,
      passwordEliminar,
      loadingEliminar,
      errorEliminar,
      eliminarCuenta,
      getSecurityColor,
      loadingEmail,
      mensajeEmail,
      probarEmail,
      loadingPush,
      mensajePush,
      pushEstado,
      activarNotificacionesMovil,
      desactivarNotificacionesMovil,
      enviarNotificacionPrueba,
      fitdaysForm,
      fitdaysConfig,
      loadingFitdays,
      mensajeFitdays,
      errorFitdays,
      guardarFitdays,
      desconectarFitdays,
      corosForm,
      corosConfig,
      loadingCoros,
      mensajeCoros,
      errorCoros,
      guardarCoros,
      desconectarCoros,
      tpForm,
      tpConfig,
      loadingTp,
      mensajeTp,
      errorTp,
      guardarTp,
      desconectarTp,
      tickForm,
      tickConfig,
      loadingTick,
      mensajeTick,
      errorTick,
      guardarTick,
      desconectarTick,
      diagnosticoTick,
      diagnosticoTickTexto,
      calEstado,
      loadingCal,
      errorCal,
      conectarCal,
      desconectarCal,
      krakenForm,
      krakenConfig,
      loadingKraken,
      mensajeKraken,
      errorKraken,
      guardarKraken,
      desconectarKraken,
      onchainForm,
      onchainConfig,
      loadingOnchain,
      mensajeOnchain,
      errorOnchain,
      guardarOnchain,
      desconectarOnchain,
      notionForm,
      notionConfig,
      loadingNotion,
      mensajeNotion,
      errorNotion,
      guardarNotion,
      desconectarNotion,
      raindropForm,
      raindropConfig,
      loadingRaindrop,
      mensajeRaindrop,
      errorRaindrop,
      guardarRaindrop,
      desconectarRaindrop
    };
  }
};
</script> 